import type { LinkView } from "../api/schemas/notes";
import { safeUrl } from "./safeUrl";

/**
 * Maps `[[wiki links]]` in a rendered text run onto the server's resolution
 * (`GET /notes/:id` → `links`). The server parses and resolves; this module
 * only finds the same spans again and looks each up by its exact `raw` text,
 * so resolution never drifts between server and client. A span the server
 * did not report (the draft in the editor, or a link in code the server
 * skipped) stays plain text. Everything returned is text or data: the page
 * renders it as text nodes and attributes, never HTML.
 */

// Same pattern as the server's core/a2a/wikiLinks.ts: innermost [[...]], no newlines.
const WIKILINK_RE = /!?\[\[[^[\]\n]+\]\]/g;

export type Segment = string | LinkView;

/** The text split into plain runs and the links the server reported. */
export const wikiSegments = (
	text: string,
	links: ReadonlyArray<LinkView>,
): ReadonlyArray<Segment> => {
	const byRaw = new Map(links.map((link) => [link.raw, link]));
	const out: Array<Segment> = [];
	let last = 0;
	for (const match of text.matchAll(WIKILINK_RE)) {
		const link = byRaw.get(match[0]);
		if (!link) continue;
		const at = match.index ?? 0;
		if (at > last) out.push(text.slice(last, at));
		out.push(link);
		last = at + match[0].length;
	}
	if (last < text.length) out.push(text.slice(last));
	return out;
};

/** What the reader sees: the alias, else the target as written. */
export const linkLabel = (link: LinkView): string => link.alias ?? link.target;

/** "#Q4" or "#Plan › Risks" for `[[Note#Plan#Risks]]`; empty without a heading. */
export const headingLabel = (link: LinkView): string =>
	link.heading ? `#${link.heading.split("#").join(" › ")}` : "";

/** One entry per distinct link, first occurrence first (the links summary). */
export const distinctLinks = (
	links: ReadonlyArray<LinkView>,
): ReadonlyArray<LinkView> => [
	...new Map(links.map((link) => [link.raw, link])).values(),
];

/** Counts per state, for the links summary. */
export const linkCounts = (links: ReadonlyArray<LinkView>) => {
	const counts = { resolved: 0, ambiguous: 0, broken: 0 };
	for (const link of distinctLinks(links)) counts[link.status] += 1;
	return counts;
};

/** An in-app href, kept only if it is http(s) or relative (ids come from data). */
export const safeHref = (href: string): string | undefined => safeUrl(href);

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The body as the read view shows it: the header already carries the title
 * and tags, so a leading `# <title>` line and a following line of only
 * those `#tags` are dropped. Anything else is left as written.
 */
export const readBody = (
	title: string,
	tags: ReadonlyArray<string>,
	body: string,
): string => {
	const noTitle = title.trim()
		? body.replace(
				new RegExp(
					`^\\s*#[ \\t]+${escapeRe(title.trim())}[ \\t]*#*[ \\t]*(\\n|$)`,
					"i",
				),
				"",
			)
		: body;
	const known = new Set(tags.map((t) => t.toLowerCase()));
	const tagLine = noTitle.match(/^\s*((?:#[A-Za-z][\w/-]*[ \t]*)+)(\n|$)/);
	const onlyKnown =
		tagLine?.[1]
			?.trim()
			.split(/\s+/)
			.every((t) => known.has(t.slice(1).toLowerCase())) ?? false;
	return (
		tagLine && onlyKnown ? noTitle.slice(tagLine[0].length) : noTitle
	).replace(/^\s+/, "");
};

/** A backlink snippet without block markdown (`#`, `-`, `>`, `1.`, `**`), one line. */
export const plainSnippet = (snippet: string): string =>
	snippet
		.replace(/^[ \t]*(?:#{1,6}[ \t]+|[-*+][ \t]+|>[ \t]?|\d+\.[ \t]+)/gm, "")
		.replace(/\*\*|__/g, "")
		.replace(/\s+/g, " ")
		.trim();
